import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { LANGUAGE_OPTIONS } from '../utils/language';
import './Footer.css';

/* Tiny 18×16 cow glyph — ambient brand cue beside the copyright.
 * Side-profile silhouette: body + head + horns + legs + a white spot.
 * ``currentColor`` so the glyph picks up the same ghost-text tone as
 * the surrounding copyright string. */
const CowGlyph: React.FC = () => (
  <svg
    className="footer-cow"
    width="18"
    height="16"
    viewBox="0 0 18 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="3" y="6" width="10" height="6" rx="1.5" fill="currentColor" />
    <rect x="12" y="5.5" width="4" height="4" rx="1" fill="currentColor" />
    <ellipse cx="12.5" cy="4.5" rx="0.7" ry="1" fill="currentColor" />
    <ellipse cx="15.5" cy="4.5" rx="0.7" ry="1" fill="currentColor" />
    <path
      d="M13 5 L13 3.5 M15 5 L15 3.5"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
    />
    <rect x="13" y="8" width="3" height="1.5" rx="0.5" fill="#ffffff" opacity="0.45" />
    <rect x="4" y="12" width="1.2" height="3" fill="currentColor" />
    <rect x="6.5" y="12" width="1.2" height="3" fill="currentColor" />
    <rect x="9" y="12" width="1.2" height="3" fill="currentColor" />
    <rect x="11.5" y="12" width="1.2" height="3" fill="currentColor" />
    <ellipse cx="6" cy="8.5" rx="1.3" ry="1" fill="#ffffff" opacity="0.5" />
    <ellipse cx="10" cy="9.5" rx="1" ry="0.8" fill="#ffffff" opacity="0.4" />
    <path
      d="M3 7.5 Q1.5 7 1.5 5.5"
      stroke="currentColor"
      strokeWidth="0.7"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <footer className="app-footer">
      <div className="footer-content">
        <div className="footer-left">
          <span className="copyright">
            <CowGlyph />
            <span>{t('footer.copyright')}</span>
          </span>
          <nav className="footer-links">
            <a href="/impressum">{t('footer.impressum')}</a>
            <a href="/terms">{t('footer.terms')}</a>
            <a href="/privacy">{t('footer.privacy')}</a>
          </nav>
        </div>

        <div className="footer-right">
          {/* Compact pill switcher — outlined chip wraps an outlined
           * globe + native select + outlined chevron. The native
           * ``<select>`` provides the dropdown behaviour and keyboard
           * a11y; the chevron is purely visual (``aria-hidden``) to
           * telegraph clickability. Both icons share a matched 1.75
           * stroke-width so they read as a single outlined pair. */}
          <div className="language-selector">
            <Globe
              className="language-icon"
              size={13}
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <select
              id="language"
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="language-select"
              aria-label={t('footer.language')}
            >
              {/* HOF-696 D19: one list for every picker (here and the worker header). */}
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown
              className="language-chevron"
              size={12}
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
